import { z } from "zod";
import { counterpartyRoleSchema, limitPolicySchema } from "../counterparties/counterparties.js";
import { expenseDocKindSchema } from "../expenses/expenses.js";
import { taxRateCodeSchema } from "../masterdata/tax.js";
import { moneySchema, quantitySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { shiftCreditSaleKindSchema } from "./enums.js";
/**
 * Vardiya İÇİ kayıtlar (Prompt 13 — API A): ara teslim, kasadan masraf, veresiye / filo fişi, test çekişi.
 * Hepsi vardiya `OPEN` ya da `CLOSING` iken girilir; aksi hâlde `409 SHIFT_NOT_OPEN`.
 *
 * Hiçbiri başka modülün tablosuna DOĞRUDAN yazmaz (AGENTS.md §3.15):
 *  · ara teslim  → `TreasuryLedgerService.record` (EXTERNAL; fişi gün sonu özet fişi taşır)
 *  · masraf      → `ExpensesService.createInTx` (`paymentMethod: "CASH"`)
 *  · veresiye    → `CounterpartyLedgerService.createOpenItem` (muhasebe fişi KESİLMEZ)
 *  · test çekişi → akaryakıt alım çekirdeği (`is_test` satışı)
 */
const reasonSchema = z.string().trim().min(3).max(500);
const noteSchema = z.string().trim().min(1).max(500);
const positiveMoneySchema = moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır");
export const denominationSchema = z.object({
    value: moneySchema.describe("Küpür değeri (kuruş)"),
    quantity: z.number().int().min(0).max(100000),
});
// ── Ara teslim (kasaya nakit bırakma) ────────────────────────────────────────
export const createShiftCashDropSchema = z.object({
    memberId: uuidSchema,
    amount: positiveMoneySchema,
    denominations: z.array(denominationSchema).max(30).optional(),
    droppedAt: timestampSchema.optional().describe("Yoksa şimdi"),
    note: noteSchema.optional(),
});
export const shiftCashDropSchema = z.object({
    id: uuidSchema,
    shiftId: uuidSchema,
    memberId: uuidSchema,
    staffName: z.string().describe("KVKK: kişisel veri — yalnızca bu yanıtta"),
    cashRegisterId: uuidSchema,
    cashRegisterName: z.string(),
    amount: moneySchema,
    denominations: z.array(denominationSchema).nullable(),
    droppedAt: timestampSchema,
    treasuryTransactionId: uuidSchema.nullable(),
    note: z.string().nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdAt: timestampSchema,
});
export const shiftCashDropListSchema = z.object({ items: z.array(shiftCashDropSchema) });
// ── Kasadan masraf ───────────────────────────────────────────────────────────
/**
 * Gider belgesinin girdisi vardiyadan TÜRETİLİR: şirket, istasyon, kasa ve ödeme biçimi üyeden gelir;
 * kullanıcı yalnızca giderin kendi alanlarını gönderir (gider sözleşmesinin zorunlu alanları).
 */
export const createShiftCashExpenseSchema = z.object({
    memberId: uuidSchema,
    categoryId: uuidSchema,
    description: z.string().trim().min(1).max(500),
    netAmount: positiveMoneySchema.describe("Matrah (KDV hariç, kuruş)"),
    vatRateCode: taxRateCodeSchema,
    withholdingRateCode: taxRateCodeSchema.nullable().optional(),
    docKind: expenseDocKindSchema.default("RECEIPT"),
    documentNo: z.string().trim().max(60).nullable().optional().describe("Fiş / makbuz no"),
    fileId: uuidSchema.nullable().optional().describe("Belge görseli (`files`, amaç DOCUMENT)"),
    occurredAt: timestampSchema.optional().describe("Yoksa şimdi"),
});
export const shiftCashExpenseSchema = z.object({
    id: uuidSchema,
    shiftId: uuidSchema,
    memberId: uuidSchema,
    staffName: z.string(),
    expenseId: uuidSchema,
    expenseDocNo: z.string().nullable(),
    categoryId: uuidSchema,
    description: z.string(),
    /** KDV dâhil ödenen (giderin `payableAmount`'ı). */
    amount: moneySchema,
    occurredAt: timestampSchema,
    cancelledAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
});
export const shiftCashExpenseListSchema = z.object({ items: z.array(shiftCashExpenseSchema) });
// ── Veresiye / filo fişi ─────────────────────────────────────────────────────
export const createShiftCreditSaleSchema = z.object({
    memberId: uuidSchema,
    kind: shiftCreditSaleKindSchema,
    counterpartyId: uuidSchema,
    slipNo: z.string().trim().min(1).max(40),
    amount: positiveMoneySchema.describe("KDV dâhil"),
    plate: z.string().trim().max(20).nullable().optional().describe("KVKK: kişisel veri olabilir"),
    productId: uuidSchema.nullable().optional(),
    nozzleId: uuidSchema.nullable().optional(),
    litres: quantitySchema.nullable().optional(),
    occurredAt: timestampSchema.optional().describe("Yoksa şimdi"),
    note: noteSchema.optional(),
    overrideCreditLimit: z
        .boolean()
        .default(false)
        .describe("Limit aşımına rağmen fişi kes — yalnızca `shifts.close.manage` izniyle"),
    // Filo fişi (Prompt 17, ADR 0017 §14 — kasasız istasyon): araç, sürücü, km ve kural onayı. YALNIZCA `kind = FLEET`.
    vehicleId: uuidSchema
        .nullable()
        .optional()
        .describe("Filo aracı; verilmezse `plate` carinin etkin araçlarında aranır"),
    fleetDriverId: uuidSchema.nullable().optional().describe("Okutulan / seçilen filo sürücüsü (sürücü kartı)"),
    odometerKm: z.number().int().min(0).max(9_999_999).nullable().optional().describe("Kilometre sayacı"),
    fleetOverride: z
        .boolean()
        .optional()
        .describe("Onaya düşen (APPROVAL) filo kuralı ihlalini geç — yalnızca `fleet.rule.override` (step-up) ile"),
});
/** Fiş anındaki limit fotoğrafı — belgeye DONDURULUR (sonradan limit değişse de kayıt değişmez). */
export const shiftCreditCheckSchema = z.object({
    verdict: z.enum(["ALLOWED", "WARN", "APPROVAL_REQUIRED", "BLOCKED"]),
    reasons: z.array(z.enum(["COUNTERPARTY_BLOCKED", "CREDIT_LIMIT_EXCEEDED", "COUNTERPARTY_INACTIVE"])),
    creditLimit: moneySchema.nullable(),
    totalRisk: moneySchema,
    riskAfter: moneySchema,
    overridden: z.boolean().describe("Yetkili limit aşımını onayladı"),
    checkedAt: timestampSchema,
});
export const shiftCreditSaleSchema = z.object({
    id: uuidSchema,
    shiftId: uuidSchema,
    memberId: uuidSchema,
    staffName: z.string(),
    kind: shiftCreditSaleKindSchema,
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    slipNo: z.string(),
    plate: z.string().nullable(),
    productId: uuidSchema.nullable(),
    nozzleId: uuidSchema.nullable(),
    litres: quantitySchema.nullable(),
    amount: moneySchema,
    occurredAt: timestampSchema,
    creditCheck: shiftCreditCheckSchema.nullable(),
    openItemId: uuidSchema.nullable(),
    note: z.string().nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdAt: timestampSchema,
    // Filo fişi alanları (Prompt 17): elle girilen filo fişinde araç / sürücü / km ve SUNUCUNUN kural kararı.
    vehicleId: uuidSchema.nullable().optional(),
    fleetDriverId: uuidSchema.nullable().optional(),
    odometerKm: z.number().int().nullable().optional(),
    fleetAuthorization: z
        .object({
        verdict: z.enum(["ALLOWED", "OVERRIDDEN"]),
        action: z.enum(["NONE", "BLOCK", "APPROVAL"]),
        violations: z.array(z.object({ code: z.string(), message: z.string() }).loose()),
        warnings: z.array(z.string()),
    })
        .loose()
        .nullable()
        .optional()
        .describe("Fiş anındaki filo kuralı kararı (dondurulur)"),
});
export const shiftCreditSaleListSchema = z.object({ items: z.array(shiftCreditSaleSchema) });
/**
 * Fiş penceresinin cari seçicisi (Prompt 17): vardiyanın ŞİRKETİNE bağlı, ETKİN, fiş türüne uygun roldeki cariler
 * (`FLEET` → `FLEET_CUSTOMER`, `ON_ACCOUNT` → `CUSTOMER`) ve şirketteki risk / limit özeti. Yetki fişi KESEN izindir
 * (`shifts.manage` ya da kendi kaydı için `shifts.close.own`, vardiyanın istasyonunda) — `counterparty.card.read`
 * gerekmez. KVKK: yalnızca kod + unvan döner (VKN / TCKN, adres, telefon, e-posta, IBAN YOK).
 */
export const listShiftCreditCounterpartiesQuerySchema = z.object({
    kind: shiftCreditSaleKindSchema,
    q: z.string().trim().min(1).max(100).optional().describe("Cari kodu ya da unvanında geçen metin"),
    counterpartyId: uuidSchema.optional().describe("Tek cari (seçili değerin etiketi / limit özeti için)"),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const shiftCreditCounterpartySchema = z.object({
    counterpartyId: uuidSchema,
    code: z.string(),
    name: z.string().describe("Unvan (gerçek kişide ad — seçim için gerekli tek kişisel alan)"),
    roles: z.array(counterpartyRoleSchema),
    isBlocked: z.boolean(),
    limitPolicy: limitPolicySchema,
    creditLimit: moneySchema.nullable(),
    totalRisk: moneySchema.describe("Vardiyanın şirketindeki toplam risk (açık bakiye + çek + sipariş)"),
    available: moneySchema.nullable().describe("Kullanılabilir limit (limit yoksa null)"),
});
export const shiftCreditCounterpartyListSchema = z.object({
    companyId: uuidSchema,
    items: z.array(shiftCreditCounterpartySchema),
});
// ── Test çekişi ──────────────────────────────────────────────────────────────
export const createShiftTestDrawSchema = z.object({
    memberId: uuidSchema,
    nozzleId: uuidSchema,
    litres: quantitySchema.refine((value) => Number(value) > 0, "Litre sıfırdan büyük olmalıdır"),
    returnedToTank: z.boolean().default(true),
    occurredAt: timestampSchema.optional().describe("Yoksa şimdi"),
    note: noteSchema.optional(),
});
/**
 * Test çekişinin kendi tablosu YOKTUR: kayıt akaryakıt satış akışına `is_test` satırı olarak yazılır
 * (tutar 0 — test yakıtı gelir değildir; beklenen tutardan zaten litre bazında düşülür).
 */
export const shiftTestDrawSchema = z.object({
    transactionId: uuidSchema,
    shiftId: uuidSchema,
    memberId: uuidSchema,
    nozzleId: uuidSchema,
    nozzleLabel: z.string(),
    productId: uuidSchema,
    litres: quantitySchema,
    returnedToTank: z.boolean(),
    occurredAt: timestampSchema,
});
export const shiftTestDrawListSchema = z.object({ items: z.array(shiftTestDrawSchema) });
// ── İptaller ─────────────────────────────────────────────────────────────────
export const cancelShiftRecordSchema = z.object({ reason: reasonSchema });
export const shiftRecordParamsSchema = z.object({ id: uuidSchema, recordId: uuidSchema });
//# sourceMappingURL=records.js.map